import { motion } from "framer-motion";
import { Minus, Plus, Trash2, ArrowRight } from "lucide-react";
import { GST_RATE } from "@/data/services";
import { scrollToId } from "@/lib/scroll";

const InteractiveEstimator = ({ items, onSetQty, onClear, onBook }) => {
  const subtotal = items.reduce((sum, i) => sum + i.price * i.qty, 0);
  const gst = subtotal * GST_RATE;
  const total = subtotal + gst;

  const handleBook = () => {
    const lines = items.map((i) => `${i.name} x${i.qty} ($${i.price} + GST ${i.unit})`);
    const summary = `Estimate request — ${lines.join("; ")}. Est. total $${total.toFixed(2)} incl. GST.`;
    onBook(summary);
    scrollToId("booking");
  };

  return (
    <section id="estimator" data-testid="estimator-section" className="relative py-20 sm:py-28 bg-surface/40 border-y border-edge">
      <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16">
        <motion.div
          initial={{ opacity: 0, y: 32 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
          className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-start"
        >
          <div>
            <div className="font-mono2 text-[11px] uppercase tracking-[0.35em] text-volt mb-4">
              02 / Instant Estimate
            </div>
            <h2 className="font-syne font-extrabold uppercase tracking-tight text-3xl sm:text-4xl lg:text-5xl text-slate-100 leading-tight">
              Build your <span className="text-outline">quote.</span>
            </h2>
            <p className="mt-4 text-sm sm:text-base text-slate-400 max-w-md">
              Tap the plus button on any service above to add it here. Adjust quantities —
              four rims, two seats, one pair of headlights — and see the total instantly.
            </p>
            {items.length > 0 && (
              <button
                data-testid="estimator-clear-btn"
                onClick={onClear}
                className="mt-6 flex items-center gap-2 font-mono2 text-[11px] uppercase tracking-[0.25em] text-slate-500 hover:text-red-400 transition-colors"
              >
                <Trash2 size={13} /> Clear estimate
              </button>
            )}
          </div>

          <div className="bg-panel border border-edge rounded-2xl p-6 sm:p-8">
            {items.length === 0 ? (
              <div data-testid="estimator-empty" className="py-14 text-center">
                <div className="font-syne font-bold uppercase tracking-widest text-slate-500 text-sm">
                  No services selected yet
                </div>
                <div className="font-mono2 text-[11px] text-slate-600 mt-2 uppercase tracking-widest">
                  Add services from the catalogue above
                </div>
              </div>
            ) : (
              <>
                <ul className="divide-y divide-edge">
                  {items.map((i) => (
                    <li key={i.name} data-testid={`estimator-row-${i.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`} className="py-4 flex items-center justify-between gap-4">
                      <div className="min-w-0">
                        <div className="font-syne font-bold text-sm uppercase tracking-wide text-slate-100 truncate">
                          {i.name}
                        </div>
                        <div className="font-mono2 text-[10px] text-slate-500 uppercase tracking-widest">
                          ${i.price} + GST · {i.unit}
                        </div>
                      </div>
                      <div className="flex items-center gap-3 shrink-0">
                        <button
                          data-testid={`estimator-dec-${i.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                          onClick={() => onSetQty(i.name, i.qty - 1)}
                          aria-label={`Decrease ${i.name} quantity`}
                          className="w-7 h-7 rounded-full border border-edge text-slate-300 hover:border-volt hover:text-volt flex items-center justify-center transition-colors"
                        >
                          <Minus size={12} />
                        </button>
                        <span className="font-mono2 text-sm text-slate-100 w-5 text-center">{i.qty}</span>
                        <button
                          data-testid={`estimator-inc-${i.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                          onClick={() => onSetQty(i.name, i.qty + 1)}
                          aria-label={`Increase ${i.name} quantity`}
                          className="w-7 h-7 rounded-full border border-edge text-slate-300 hover:border-volt hover:text-volt flex items-center justify-center transition-colors"
                        >
                          <Plus size={12} />
                        </button>
                      </div>
                    </li>
                  ))}
                </ul>
                <div className="mt-6 pt-5 border-t border-edge space-y-2 font-mono2 text-xs uppercase tracking-widest">
                  <div className="flex justify-between text-slate-400">
                    <span>Subtotal</span>
                    <span data-testid="estimator-subtotal">${subtotal.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between text-slate-400">
                    <span>GST (10%)</span>
                    <span data-testid="estimator-gst">${gst.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between text-slate-50 text-sm pt-2 border-t border-edge">
                    <span className="font-syne font-bold">Estimated Total</span>
                    <span data-testid="estimator-total-price" className="font-syne font-extrabold text-volt">
                      ${total.toFixed(2)}
                    </span>
                  </div>
                </div>
                <button
                  data-testid="estimator-book-now-btn"
                  onClick={handleBook}
                  className="mt-7 w-full flex items-center justify-center gap-2 bg-volt text-carbon font-syne font-bold text-sm uppercase tracking-widest rounded-full px-6 py-4 hover:bg-white hover:text-carbon transition-colors duration-300"
                >
                  Book This Estimate <ArrowRight size={16} />
                </button>
              </>
            )}
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default InteractiveEstimator;
